// src/pages/internal/clientesSwap/serviceItem.js
import {
	Grid,
	useTheme,
	Box,
	Button,
	Tabs,
	Tab,
	Divider,
	Paper,
	Typography,
	darken,
	lighten
} from "@mui/material";
import useStyles from "./styles";
import { memo, useState, useEffect, useCallback, useRef } from "react";
import FormClientesSwap from "./swapForm";
import { HttpsOutlined } from "@mui/icons-material";

import PreviewData from "./previewData";
import MapViewer from "./mapa";
import UploadScreen, { MAX_TOTAL_SIZE_BYTES, formatBytes, getFileSize } from "./anexos";
import InternoModalDeleteCliente from "../../../components/modalSaindoDaPagina";
import FormWoman from "../../../components/artsPersonComponent/formWoman";

// =========================================================================
// FUNÇÕES UTILITÁRIAS PARA CONVERSÃO DE ARQUIVOS EM BASE64
// =========================================================================
const fileToBase64 = (file) => new Promise((resolve, reject) => {
	const reader = new FileReader();
	reader.readAsDataURL(file);
	reader.onload = () => resolve(reader.result.split(',')[1]);
	reader.onerror = error => reject(error);
});
// =========================================================================

const ServiceItem = memo(
	({
		trecho,
		setChanged = () => { },
		editMode = false,
		setEditMode = () => { },
		selectedTitle = "",
		selectedSubTitle = "",
		setSnackbar,
		tiposDeSwap,
		createMode = false,
		setCreateMode = () => { },
		onCreate = () => { },
		onUpdate = () => { },
		externalLoading = false,
		setHasUnsavedChanges,
		updateOptions = () => { },
		activeTab = 0,
		setActiveTab = () => { },
	}) => {
		const theme = useTheme();
		const styles = useStyles(theme);
		const [loading, setLoading] = useState(false);

		const [clientesSwap, setClientesSwap] = useState(null);
		const [clientesSwapKMZ, setClientesSwapKMZ] = useState(null);
		const [clientesSwapAnexos, setClientesSwapAnexos] = useState([]);
		const [originalDataStr, setOriginalDataStr] = useState(null);
		const [originalAnexosStr, setOriginalAnexosStr] = useState(null);
		const [originalKmzStr, setOriginalKmzStr] = useState(null);
		const baselineCapturedRef = useRef(false);
		const isSaving = loading || externalLoading;

		const [formErrors, setFormErrors] = useState({ nome: false, subclientes: [] });
		const [openCancelModal, setOpenCancelModal] = useState(false);

		// Fingerprint estável para anexos (cobre KMZ que também fica em clientesSwapAnexos)
		const fingerprintAnexos = useCallback((arr) => JSON.stringify(
			(arr || []).map((a) => ({
				id: a?.id || null,
				name: a?.name || a?.nome || "",
				isNew: !!a?.isNew,
				size: a?.size || a?.tamanho || a?.originalFile?.size || 0,
			}))
		), []);

		const fingerprintKmz = useCallback((kmz) => kmz
			? JSON.stringify({
				id: kmz?.id || null,
				name: kmz?.name || kmz?.nome || "",
				isNew: !!kmz?.isNew,
				size: kmz?.size || 0,
			})
			: "null", []);

		// =========================================================================
		// FUNÇÃO DE RESET PARA CANCELAMENTO
		// =========================================================================
		const resetToOriginalData = useCallback(() => {
			if (trecho) {
				const {
					clientesSwapAnexos: anexosDoJson,
					clientesSwapKMZs: kmzsDoJson,
					...resto
				} = trecho;

				const restoClone = JSON.parse(JSON.stringify(resto));

				setClientesSwapKMZ(kmzsDoJson || null);
				setClientesSwapAnexos(anexosDoJson ? [...anexosDoJson] : []);
				setClientesSwap(restoClone);

				// Baseline será capturado pelo efeito abaixo, depois que o
				// swapForm normalizar os defaults (evita falso positivo de dirty).
				setOriginalDataStr(null);
				setOriginalAnexosStr(null);
				setOriginalKmzStr(null);
				baselineCapturedRef.current = false;
				if (setHasUnsavedChanges) setHasUnsavedChanges(false);
			}
		}, [trecho, setHasUnsavedChanges]);

		useEffect(() => {
			if (trecho) {
				resetToOriginalData();
			}
			else if (createMode && clientesSwap === null) {
				setClientesSwap(null);
				setClientesSwapKMZ(null);
				setClientesSwapAnexos([]);
				setOriginalDataStr(null);
				setOriginalAnexosStr(null);
				setOriginalKmzStr(null);
				baselineCapturedRef.current = false;
				if (setHasUnsavedChanges) setHasUnsavedChanges(false);
			}
		}, [trecho, createMode, resetToOriginalData, setHasUnsavedChanges]);

		// =========================================================================
		// CAPTURA LAZY DO BASELINE (com debounce) — espera o swapForm
		// terminar de aplicar valores padrão antes de fixar o "estado original".
		// =========================================================================
		useEffect(() => {
			if (!editMode) return;
			if (!clientesSwap) return;
			if (baselineCapturedRef.current) return;

			const t = setTimeout(() => {
				setOriginalDataStr(JSON.stringify(clientesSwap));
				setOriginalAnexosStr(fingerprintAnexos(clientesSwapAnexos));
				setOriginalKmzStr(fingerprintKmz(clientesSwapKMZ));
				baselineCapturedRef.current = true;
				if (setHasUnsavedChanges) setHasUnsavedChanges(false);
			}, 120);

			return () => clearTimeout(t);
		}, [editMode, clientesSwap, clientesSwapAnexos, clientesSwapKMZ, fingerprintAnexos, fingerprintKmz, setHasUnsavedChanges]);

		const handleTabChange = (event, newValue) => setActiveTab(newValue);

		// Quando sai do modo de edição, reseta para que ao reentrar o baseline seja recapturado.
		useEffect(() => {
			if (!editMode) {
				baselineCapturedRef.current = false;
				setOriginalDataStr(null);
				setOriginalAnexosStr(null);
				setOriginalKmzStr(null);
			}
		}, [editMode]);

		// =========================================================================
		// DETECTAR ALTERAÇÕES (DIRTY CHECK)
		// =========================================================================
		const checkIsDirty = () => {
			if (!clientesSwap) return false;

			if (editMode) {
				if (!baselineCapturedRef.current || !originalDataStr) return false;
				return (
					JSON.stringify(clientesSwap) !== originalDataStr ||
					fingerprintAnexos(clientesSwapAnexos) !== originalAnexosStr ||
					fingerprintKmz(clientesSwapKMZ) !== originalKmzStr
				);
			}
			if (createMode) {
				// Checa se o usuário preencheu pelo menos algum dado básico
				return !!(
					clientesSwap.nome ||
					clientesSwap.cnl ||
					(clientesSwap.subclientes && clientesSwap.subclientes.length > 0) ||
					(clientesSwap.swapTypes && clientesSwap.swapTypes.some(t => t.value)) ||
					(clientesSwapAnexos && clientesSwapAnexos.length > 0) ||
					!!clientesSwapKMZ
				);
			}
			return false;
		};

		useEffect(() => {
			if (setHasUnsavedChanges) {
				setHasUnsavedChanges(checkIsDirty());
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [clientesSwap, clientesSwapAnexos, clientesSwapKMZ, editMode, createMode, originalDataStr, originalAnexosStr, originalKmzStr]);

		// =========================================================================
		// AÇÕES DO BOTÃO CANCELAR
		// =========================================================================
		const handleCancelClick = () => {
			if (checkIsDirty()) {
				setOpenCancelModal(true);
			} else {
				executeCancel();
			}
		};

		const executeCancel = () => {
			resetToOriginalData();
			setFormErrors({ nome: false, subclientes: [] });
			setEditMode(false);
			setCreateMode(false);
			setOpenCancelModal(false);
			if (setHasUnsavedChanges) setHasUnsavedChanges(false);
		};


		const validateForm = () => {
			let hasError = false;
			const errors = { nome: false, subclientes: [] };

			if (!clientesSwap?.nome || clientesSwap.nome.trim() === "") {
				errors.nome = true;
				hasError = true;
			}

			(clientesSwap?.subclientes || []).forEach((sub, index) => {
				if (!sub.nome || sub.nome.trim() === "") {
					errors.subclientes.push(index);
					hasError = true;
				}
			});

			setFormErrors(errors);

			if (hasError) {
				setSnackbar({ children: "Por favor, preencha todos os campos obrigatórios.", severity: "warning" });
				return false;
			}

			const totalAnexos = (clientesSwapAnexos || []).reduce((sum, f) => sum + getFileSize(f), 0);
			if (totalAnexos > MAX_TOTAL_SIZE_BYTES) {
				setSnackbar({
					children: `O tamanho total dos anexos (${formatBytes(totalAnexos)}) excede o limite de ${formatBytes(MAX_TOTAL_SIZE_BYTES)}. Remova arquivos para conseguir salvar.`,
					severity: "error",
				});
				return false;
			}

			return true;
		};

		const formatPayload = async () => {
			const optionsSelecionadas = (clientesSwap?.swapTypes || [])
				.filter(opt => opt.value === true)
				.map(opt => ({
					optionId: opt.id || opt.optionId,
					value: true
				}));

			const anexosFormatados = await Promise.all(
				(clientesSwapAnexos || []).map(async (anexo) => {
					if (anexo.id) return anexo;
					return {
						nome: anexo.name || anexo.nome || 'arquivo',
						mimeType: anexo.type || 'application/octet-stream',
						dadosBase64: await fileToBase64(anexo.originalFile)
					};
				})
			);

			return {
				clienteSwap: {
					id: clientesSwap?.id ?? trecho?.id,
					nome: clientesSwap?.nome,
					cnl: clientesSwap?.cnl,
					serviceId: clientesSwap?.serviceId,
					distancia: clientesSwap?.distancia,
					quantidadeDeFibras: clientesSwap?.quantidadeDeFibras,
					totalDeFibras: clientesSwap?.totalDeFibras,
					capacidade: clientesSwap?.capacidade,
					urs: clientesSwap?.urs,
					observacao: clientesSwap?.observacao,
					parceiroSwapId: clientesSwap?.parceiroSwapId,
					cedenteSwapId: clientesSwap?.cedenteSwapId,
					subclientes: clientesSwap?.subclientes || []
				},
				rlClientesSwapOptions: optionsSelecionadas,
				clientesSwapAnexos: anexosFormatados,
				clientesAfetados: (clientesSwap?.clientesAfetados || [])
					.map(c => ({ clienteId: c?.id || c?.clienteId }))
					.filter(c => c.clienteId)
			};
		};

		const handleSaveCreate = async () => {
			if (!validateForm()) return;

			setLoading(true);
			try {
				const payload = await formatPayload();
				await onCreate(payload);
			} catch (error) {
				console.error("Erro ao formatar os dados:", error);
				setSnackbar({ children: "Erro ao processar arquivos", severity: "error" });
			} finally {
				setLoading(false);
			}
		};

		const handleSaveUpdate = async () => {
			if (!validateForm()) return;

			const idParaUpdate = clientesSwap?.id ?? trecho?.id;

			if (!idParaUpdate) {
				setSnackbar({ children: "ID do trecho não encontrado. Tente recarregar a página.", severity: "error" });
				return;
			}

			setLoading(true);
			try {
				const payload = await formatPayload();
				await onUpdate(payload);
			} catch (error) {
				console.error("Erro ao formatar os dados:", error);
				setSnackbar({ children: "Erro ao processar arquivos", severity: "error" });
			} finally {
				setLoading(false);
			}
		};

		const TabCustom = ({ index, labelText, ...props }) => (
			<Tab
				label={<Box sx={styles.tabLabel}>{labelText}</Box>}
				sx={{
					...styles.tab,
					...(activeTab === index && {
						borderColor: "divider",
						backgroundColor: theme.palette.background.paper,
					}),
					...(activeTab !== index && {
						backgroundColor:
							theme.palette.mode === "dark"
								? darken(theme.palette.background.paper, 0.05)
								: lighten(theme.palette.secondary.main, 0.95),
					}),
					"&:hover": {
						borderColor:
							activeTab === index
								? "divider"
								: darken(theme.palette.background.paper, 0.5),
						backgroundColor:
							activeTab === index
								? "background.paper"
								: lighten(theme.palette.background.paper, 0.05),
					},
				}}
				{...props}
			/>
		);

		return (
			<Grid item xs={12} >
				<Box sx={styles.containerServiceItem}>
					<Box sx={styles.headStcker}>
						<Box sx={styles.boxButtons}>
							<Box sx={{ display: "flex", flexDirection: "column" }}>
								<Typography variant="body1" fontWeight={700}>
									{selectedTitle}
								</Typography>
								{!createMode && <Typography variant="body2" fontWeight={600} color="textSecondary" fontSize={"13px"}>
									CNL:{""}{selectedSubTitle}
								</Typography>}
							</Box>

							{editMode ? (
								<Box sx={{ display: "flex", gap: 1, flexDirection: "row" }}>
									<Button
										disabled={isSaving}
										variant="outlined"
										size="small"
										onClick={handleCancelClick}
									>
										Cancelar
									</Button>
									<Button
										disabled={isSaving}
										variant="contained"
										color="primary"
										size="small"
										onClick={handleSaveUpdate}
									>
										Salvar
									</Button>
								</Box>
							) :
								createMode ? (
									<Box sx={{ display: "flex", gap: 1, flexDirection: "row" }}>
										<Button
											disabled={loading}
											variant="outlined"
											size="small"
											onClick={handleCancelClick}
										>
											Cancelar
										</Button>
										<Button
											disabled={loading}
											variant="contained"
											color="primary"
											size="small"
											onClick={handleSaveCreate}
										>
											Salvar
										</Button>
									</Box>
								) :
									(
										<Button
											variant="contained"
											size="small"
											startIcon={<HttpsOutlined />}
											disabled={loading}
											onClick={() => {
												setEditMode(true);
												setChanged(true);
											}}
										>
											Editar
										</Button>
									)}
						</Box>

						{!loading && (
							<Box sx={styles.containerTabsNActionButtons}>
								<Tabs value={activeTab} onChange={handleTabChange} sx={styles.tabs} variant="scrollable" scrollButtons={false} >
									<TabCustom index={0} labelText="Formulário" value={0} />
									<TabCustom index={1} labelText="Anexo" value={1} />
									<TabCustom index={2} labelText="Mapas" value={2} />
								</Tabs>
							</Box>
						)}
						<Divider />
					</Box>

					<Paper variant="outlined" sx={{ display: "block", p: 1, borderRadius: 1, mt: 2, minHeight: "200px" }}>
						{activeTab === 0 && (
							editMode || createMode ? (
								<FormClientesSwap
									editMode={editMode}
									tiposDeSwap={tiposDeSwap}
									createMode={createMode}
									clientesSwap={clientesSwap}
									setClientesSwap={setClientesSwap}
									formErrors={formErrors}
									setFormErrors={setFormErrors}
									updateOptions={updateOptions}
								/>
							) : (
								<PreviewData form={trecho} setSnackbar={setSnackbar} />
							)
						)}
						{activeTab === 1 && (
							<Box sx={{ p: 2 }}>
								{(clientesSwapAnexos || []).filter(file => {
									const fileName = file.name || file.nome || '';
									return !fileName.toLowerCase().endsWith('.kmz');
								}).length > 0 || (createMode || editMode) ?
									<UploadScreen clientesSwapAnexos={clientesSwapAnexos} setClientesSwapAnexos={setClientesSwapAnexos} editMode={editMode} createMode={createMode} setSnackbar={setSnackbar} />
									: <FormWoman
										mainFill={theme.palette.secondary.dark}
										mainText={{ before: "Parece que ainda não\n" + "há ", highlight: "Anexos", after: " por aqui." }}
										subText={"Os anexos aparecerão\n" + "assim que forem criados"}
									/>

								}
							</Box>

						)}
						{activeTab === 2 && (
							<Box sx={{ p: 2 }}>
								{
									(clientesSwapAnexos || []).filter(file => file.name.toLowerCase().endsWith('.kmz')).length > 0 || (createMode || editMode) ?
										<MapViewer clientesSwapAnexos={clientesSwapAnexos} setClientesSwapAnexos={setClientesSwapAnexos} editMode={editMode} createMode={createMode} /> :
										<FormWoman
											mainFill={theme.palette.secondary.dark}
											mainText={{ before: "Parece que ainda não\n" + "há ", highlight: "Mapas", after: " por aqui." }}
											subText={"Os mapas aparecerão\n" + "assim que forem criados"}
										/>
								}
							</Box>
						)}
					</Paper>

					{/* MODAL DE SAÍDA / CANCELAMENTO DENTRO DO PAINEL */}
					<InternoModalDeleteCliente
						openConfirmacao={openCancelModal}
						setOpenConfirmacao={setOpenCancelModal}
						confirmLoad={false}
						execute={executeCancel}
						cancel={() => setOpenCancelModal(false)}
						title="Atenção"
						severity="warning"
						contentText="Você está prestes a sair do modo de edição. As alterações não salvas serão perdidas, deseja continuar?"
						confirmText="Continuar"
					/>

				</Box>
			</Grid>
		);
	}
);

export { ServiceItem };